import { yupResolver } from '@hookform/resolvers/yup';
import { GetEmployerDto, SubEntityDto } from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { SubEntityVariant } from '@zorro/types';
import { Stack } from '@zorro/web/design-system';
import {
  useBatchCallEndpoint,
  useForm,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import * as yup from 'yup';

import { EmployeeSubEntityInput } from '../../EmployeeSubEntityInput/EmployeeSubEntityInput';
import { FormFooter } from '../../FormFooter';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

const subEntitySchema = yup.object({
  subEntityId: yup.string().nullable(),
  subEntity2Id: yup.string().nullable(),
});

type SubEntityFormFields = yup.InferType<typeof subEntitySchema>;

type Props = {
  recordsForActions: RecordForActions[];
  employerId: GetEmployerDto['id'];
  onClose: () => void;
  onSuccess: () => void;
};

function getInitialSubEntityId(
  recordsForActions: RecordForActions[],
  subEntities: SubEntityDto[],
  fieldName: 'subEntity' | 'subEntity2'
): string | undefined {
  const uniqueValues = [
    ...new Set(recordsForActions.map((record) => record[fieldName])),
  ];

  if (uniqueValues.length !== 1 || !uniqueValues[0]) {
    return undefined;
  }

  return subEntities.find((se) => se.name === uniqueValues[0])?.id;
}

export const EmployeesSubEntityBulkEditForm = ({
  recordsForActions,
  employerId,
  onClose,
  onSuccess,
}: Props) => {
  const { executeBatchCall, errors } = useBatchCallEndpoint({
    methodName: 'employeesControllerUpdateByAdmin',
    singularItemName: 'employee',
    action: 'updated',
    batchSize: 5,
  });

  const { data: subEntities = [] } = useMonolithQuery({
    method: 'subEntityControllerFindMany',
    params: [employerId],
  });

  const { data: subEntities2 = [] } = useMonolithQuery({
    method: 'subEntity2ControllerFindMany',
    params: [employerId],
  });

  const {
    control,
    formState: { isValid },
    getValues,
  } = useForm<SubEntityFormFields>({
    mode: 'all',
    resolver: yupResolver(subEntitySchema),
    defaultValues: {
      subEntityId: getInitialSubEntityId(
        recordsForActions,
        subEntities,
        'subEntity'
      ),
      subEntity2Id: getInitialSubEntityId(
        recordsForActions,
        subEntities2,
        'subEntity2'
      ),
    },
  });

  async function handleSubmit() {
    const { subEntityId, subEntity2Id } = getValues();

    const batchItems = recordsForActions.map(({ fullName, employeeId }) => {
      return {
        key: fullName,
        params: [employeeId, { subEntityId, subEntity2Id }] as const,
      };
    });

    await executeBatchCall(batchItems, { onSuccess });
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{ onClick: handleSubmit, disabled: !isValid }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <EmployeeSubEntityInput
        control={control}
        employerId={employerId}
        name="subEntityId"
        type={SubEntityVariant.subEntity}
        selectProps={{ searchable: true }}
      />
      <EmployeeSubEntityInput
        control={control}
        name="subEntity2Id"
        employerId={employerId}
        type={SubEntityVariant.subEntity2}
        selectProps={{ searchable: true }}
      />
      <ErrorsTable
        tableTitle="Employee name"
        errors={errors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};
